// Error handling utilities for consistent user feedback

export interface ErrorInfo {
  message: string;
  type: 'error' | 'warning' | 'info';
  details?: string;
  code?: string;
}

export interface SuccessInfo {
  message: string;
  type: 'success';
  details?: string;
}

export type NotificationInfo = ErrorInfo | SuccessInfo;

// Common error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TIMEOUT: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  
  // Specific error messages
  LOGIN_FAILED: 'Login failed. Please check your credentials.',
  SAVE_FAILED: 'Failed to save changes. Please try again.',
  DELETE_FAILED: 'Failed to delete item. Please try again.',
  LOAD_FAILED: 'Failed to load data. Please refresh the page.',
  EXPORT_FAILED: 'Failed to export data. Please try again.',
  UPLOAD_FAILED: 'Failed to upload file. Please try again.',
  
  // Validation errors
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_DATE: 'Please enter a valid date.',
  INVALID_TIME: 'Please enter a valid time.',
  PASSWORD_TOO_SHORT: 'Password must be at least 6 characters long.',
  PASSWORDS_DONT_MATCH: 'Passwords do not match.',
  INVALID_FORMAT: 'Invalid format. Please check your input.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully.',
  DELETED: 'Item deleted successfully.',
  CREATED: 'Item created successfully.',
  UPDATED: 'Item updated successfully.',
  EXPORTED: 'Data exported successfully.',
  UPLOADED: 'File uploaded successfully.',
  LOGGED_IN: 'Logged in successfully.',
  LOGGED_OUT: 'Logged out successfully.',
  PASSWORD_CHANGED: 'Password changed successfully.',
  SETTINGS_SAVED: 'Settings saved successfully.',
};

// Error classification
export function classifyError(error: any): ErrorInfo {
  // Network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return {
        message: ERROR_MESSAGES.TIMEOUT,
        type: 'error',
        code: 'TIMEOUT'
      };
    }
    return {
      message: ERROR_MESSAGES.NETWORK_ERROR,
      type: 'error',
      code: 'NETWORK_ERROR',
      details: error.message
    };
  }

  // HTTP status code errors
  const status = error.response?.status;
  const message = error.response?.data?.message || error.message;

  switch (status) {
    case 400:
      return {
        message: message || ERROR_MESSAGES.VALIDATION_ERROR,
        type: 'error',
        code: 'VALIDATION_ERROR',
        details: error.response?.data?.details
      };
    case 401:
      return {
        message: ERROR_MESSAGES.UNAUTHORIZED,
        type: 'error',
        code: 'UNAUTHORIZED',
        details: message
      };
    case 403:
      return {
        message: ERROR_MESSAGES.FORBIDDEN,
        type: 'error',
        code: 'FORBIDDEN',
        details: message
      };
    case 404:
      return {
        message: ERROR_MESSAGES.NOT_FOUND,
        type: 'error',
        code: 'NOT_FOUND',
        details: message
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        message: ERROR_MESSAGES.SERVER_ERROR,
        type: 'error',
        code: 'SERVER_ERROR',
        details: message
      };
    default:
      return {
        message: message || ERROR_MESSAGES.UNKNOWN_ERROR,
        type: 'error',
        code: 'UNKNOWN_ERROR',
        details: error.message
      };
  }
}

// Success classification
export function createSuccessMessage(type: keyof typeof SUCCESS_MESSAGES, details?: string): SuccessInfo {
  return {
    message: SUCCESS_MESSAGES[type],
    type: 'success',
    details
  };
}

// Validation error helper
export function createValidationError(field: string, message?: string): ErrorInfo {
  return {
    message: message || `${field} is required.`,
    type: 'error',
    code: 'VALIDATION_ERROR'
  };
}

// API error handler
export function handleApiError(error: any, fallbackMessage?: string): ErrorInfo {
  const classified = classifyError(error);
  
  // Override with fallback message if provided
  if (fallbackMessage) {
    return {
      ...classified,
      message: fallbackMessage
    };
  }
  
  return classified;
}

// Form validation helper
export function validateForm(data: Record<string, any>, rules: Record<string, (value: any) => string | null>): ErrorInfo[] {
  const errors: ErrorInfo[] = [];
  
  Object.entries(rules).forEach(([field, validator]) => {
    const value = data[field];
    const error = validator(value);
    
    if (error) {
      errors.push({
        message: error,
        type: 'error',
        code: 'VALIDATION_ERROR'
      });
    }
  });
  
  return errors;
}

// Common validation rules
export const VALIDATION_RULES = {
  required: (value: any) => value === null || value === undefined || value === '' ? ERROR_MESSAGES.REQUIRED_FIELD : null,
  email: (value: any) => {
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? null : ERROR_MESSAGES.INVALID_EMAIL;
  },
  minLength: (min: number) => (value: any) => {
    if (!value) return null;
    return value.length >= min ? null : `Must be at least ${min} characters long.`;
  },
  passwordMatch: (password: string) => (value: any) => {
    return value === password ? null : ERROR_MESSAGES.PASSWORDS_DONT_MATCH;
  },
  date: (value: any) => {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? ERROR_MESSAGES.INVALID_DATE : null;
  },
  time: (value: any) => {
    if (!value) return null;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(value) ? null : ERROR_MESSAGES.INVALID_TIME;
  }
};

// Toast notification helper
export function getNotificationProps(notification: NotificationInfo) {
  return {
    severity: notification.type,
    message: notification.message,
    details: notification.details
  };
}

// Error boundary helper
export function getErrorBoundaryMessage(error: Error, errorInfo: any): string {
  console.error('Error Boundary caught an error:', error, errorInfo);
  
  // In production, show a generic message
  if (process.env.NODE_ENV === 'production') {
    return 'Something went wrong. Please refresh the page and try again.';
  }
  
  // In development, show more details
  return `Error: ${error.message}`;
}

// Retry helper
export function shouldRetry(error: any, retryCount: number, maxRetries: number = 3): boolean {
  // Don't retry if we've exceeded max retries
  if (retryCount >= maxRetries) return false;
  
  // Don't retry client errors (4xx)
  if (error.response?.status >= 400 && error.response?.status < 500) return false;
  
  // Retry network errors and server errors (5xx)
  return !error.response || error.response.status >= 500;
}

// Error logging helper
export function logError(error: any, context?: string) {
  const errorInfo = classifyError(error);
  
  console.error(`[${context || 'Unknown'}] Error:`, {
    message: errorInfo.message,
    code: errorInfo.code,
    details: errorInfo.details,
    originalError: error
  });
  
  // In a real application, you might want to send this to a logging service
  // like Sentry, LogRocket, or your own logging API
}

export default {
  classifyError,
  createSuccessMessage,
  createValidationError,
  handleApiError,
  validateForm,
  VALIDATION_RULES,
  getNotificationProps,
  getErrorBoundaryMessage,
  shouldRetry,
  logError,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES
};
